<template>
    <ExtendedLayout
        :project="project"
        currentTab="documentation"
    >

        <Panel>

            <Heading type="h2">Shortcodes</Heading>

            <div class="border border-gray-200 px-4 py-2 text-sm mt-2">
                <div class="divide-y border-gray-200">
                    <div v-for="shortcode in shortcodes" class="py-3">
                        <div class="font-bold">{{ shortcode.title }}</div>
                        <div class="pt-1">{{ shortcode.desc }}</div>
                    </div>
                </div>
                <div class="italic py-2">*) Requires an active license.</div>
            </div>

            <Heading type="h2" class="mt-6">Gravity Forms</Heading>

            <div class="border border-gray-200 px-4 py-2 text-sm mt-2">
                <div class="divide-y border-gray-200">
                    <div v-for="gravityForm in gravityForms" class="py-3">
                        <div class="font-bold">{{ gravityForm.title }}</div>
                        <div class="pt-1">
                            <ol class="list-decimal pl-6">
                                <li v-for="bullet in gravityForm.bullets">{{ bullet }}</li>
                            </ol>
                        </div>
                    </div>
                </div>
                <div class="italic py-2">*) Requires an active license.</div>
            </div>

            <Heading type="h2" class="mt-6">Image Map Pro</Heading>

            <div class="border border-gray-200 px-4 py-2 text-sm mt-2">
                <div class="divide-y border-gray-200">
                    <div v-for="imageMapPro in imageMapPros" class="py-3">
                        <div class="font-bold">{{ imageMapPro.title }}</div>
                        <div class="pt-1">
                            <ol class="list-decimal pl-6">
                                <li v-for="bullet in imageMapPro.bullets">{{ bullet }}</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <Heading type="h2" class="mt-6">Data Import</Heading>

            <div class="border border-gray-200 px-4 py-2 text-sm mt-2">
                <div class="divide-y border-gray-200">
                    <div v-for="dataImport in dataImports" class="py-3">
                        <div class="font-bold">{{ dataImport.title }}</div>
                        <div class="pt-1">
                            <ol class="list-decimal pl-6">
                                <li v-for="bullet in dataImport.bullets">{{ bullet }}</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

        </Panel>

    </ExtendedLayout>
</template>

<script>
import ExtendedLayout from '@/Pages/Projects/Edit';
import Panel from "@/Components/Panel";
import Heading from "@/Components/Heading";

export default {
    components: {
        Heading,
        Panel,
        ExtendedLayout,
    },
    props: {
        errors: Object,
        project: Object,
    },
    data() {
        return {
            shortcodes: [
                {
                    title: '[edc-homes]',
                    desc: 'Ouputs a list of all published homes in a table-like-structure with sortable column-headers and an AJAX-modal that retrieves the details of each home on click.',
                },
                {
                    title: '[edc-realtors]',
                    desc: 'Outputs a list of realtors.*',
                },
                {
                    title: '[edc-open-house-address]',
                    desc: 'Outputs the open house address.*',
                },
                {
                    title: '[edc-contact-phone]',
                    desc: 'Outputs the contact phone number as a clickable link*',
                },
                {
                    title: '[edc-contact-email]',
                    desc: 'Outputs the contact email address as a clickable link*',
                },
                {
                    title: '[edc-open-house-link]',
                    desc: 'Outputs a link to the open house signup page. The shortcode supports a custom anchor text by wrapping a text-block. Ie. [edc-open-house-link]My custom anchor[/edc-open-house-link].*',
                },
                {
                    title: '[edc-interest-link]',
                    desc: 'Outputs a link to the interest inquiry page. The shortcode supports a custom anchor text by wrapping a text-block. Ie. [edc-interest-link]My custom anchor[/edc-interest-link].*',
                },
                {
                    title: '[edc-reservation-link]',
                    desc: 'Outputs a link to the reservation inquiry page. The shortcode supports a custom anchor text by wrapping a text-block. Ie. [edc-interest-link]My custom anchor[/edc-reservation-link].*',
                },
                {
                    title: '[edc-action-link]',
                    desc: 'Outputs a link based on the current project status. The shortcode supports a custom anchor text by wrapping a text-block. Ie. [edc-action-link]My custom anchor[/edc-action-link].*',
                },
                {
                    title: '[edc-property id=”…”]',
                    desc: 'Outputs a card of the home-data based on the ID-attribute. This is used in conjunction with Image Map Pro overlays.',
                },
                {
                    title: '[edc-property uid=”…”]',
                    desc: 'Outputs a card of the home-data based on the UID-attribute. This is used in conjunction with Image Map Pro overlays.',
                },
                {
                    title: '[edc-download-materials id="..." label="..."]',
                    desc: 'Outputs a HTML link to the relevant case material. The "id"-parameter should be a property ID. A fallback loop will be used to determine the most relevant materials. Optionally, the "label"-parameter can be added to determine what the link text should be.',
                },
                {
                    title: '[edc-download-materials-category category="..." label="..."]',
                    desc: 'Outputs a HTML link to the relevant case material. The "category"-parameter should be a category slug. If no category slug is added, it will use the current property\'s category. Optionally, the "label"-parameter can be added to determine what the link text should be.',
                },
                {
                    title: '[edc-download-materials-project projectid="..." label="..."]',
                    desc: 'Outputs a HTML link to the relevant case material. The "projectid"-parameter should be a project ID. If no projectid is added, it will use the current property\'s project (or fallback to the root project). Optionally, the "label"-parameter can be added to determine what the link text should be.',
                },
            ],
            gravityForms: [
                {
                    title: 'Automatically insert open house dates in field*.',
                    bullets: [
                        'Create or edit a Gravity Form',
                        'Insert a “Dropdown” field',
                        'Toggle the field, and click “Advanced”',
                        'Activate “Dynamic Population”',
                        'Insert “edc_populate_open_house_dates”',
                    ],
                },
                {
                    title: 'Automatically insert homes in field*.',
                    bullets: [
                        'Create or edit a Gravity Form',
                        'Insert a “Dropdown” field',
                        'Toggle the field, and click “Advanced”',
                        'Activate “Dynamic Population”',
                        'Insert “edc_populate_homes”',
                    ],
                },
                {
                    title: 'Automatically insert property categories in field*.',
                    bullets: [
                        'Create or edit a Gravity Form',
                        'Insert a “Dropdown” field',
                        'Toggle the field, and click “Advanced”',
                        'Activate “Dynamic Population”',
                        'Insert “edc_populate_categories”',
                    ],
                },
                {
                    title: 'Automatically insert property types in field*.',
                    bullets: [
                        'Create or edit a Gravity Form',
                        'Insert a “Dropdown” field',
                        'Toggle the field, and click “Advanced”',
                        'Activate “Dynamic Population”',
                        'Insert “edc_populate_types”',
                    ],
                },
            ],
            imageMapPros: [
                {
                    title: 'Show a property in a polygon tooltip and color it by the status',
                    bullets: [
                        'Create a polygon',
                        'Name the polygon by the property ID',
                        'Edit the tooltip content',
                        'Be sure that you are working within a “paragraph” field',
                        'Insert the shortcode [edc-property id=”…”] with the ID of the property.',
                        '… or insert the shortcode [edc-property-uid uid=”…”] with the UID of the property.',
                    ],
                },
            ],
            dataImports: [
                {
                    title: 'Import project settings',
                    bullets: [
                        'Make sure your project website has the API-endpoint set and a matching auth-key.',
                        'Set up a CRON-job. You can use EasyCron or any service of your liking.',
                        'Run it against http://example.com/wp-load.php?edc_import_project_data',
                        'Recommended schedule is once every 15 minutes.',
                        'To trigger it manually, just vist the URL in the browser',
                    ],
                },
                {
                    title: 'Import homes',
                    bullets: [
                        'Make sure your project website has the API-endpoint set.',
                        'Set up a CRON-job. You can use EasyCron or any service of your liking.',
                        'Run it against http://example.com/wp-load.php?edc_import_homes',
                        'Recommended schedule is once every 60 minutes.',
                        'To trigger it manually, just vist the URL in the browser',
                    ],
                },
            ],
        };
    },
}
</script>
